// frontend/src/pages/reports/ClassificationReport.jsx
// Report 4: ABC XYZ Classification

import React, { useState, useMemo, useCallback, useRef } from 'react';
import '../../styles/staff-columns.css';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import PageBanner from '../../components/common/PageBanner';
import Tile from '../../components/layout/Tile';
import ReportKpiStrip from '../../components/reports/ReportKpiStrip';
import { ItemFilterBar, useItemFilter } from '../../features/item-filter';
import { useSsrmGrid, buildExportQuery } from '../../hooks/useSsrmGrid';
import { translateRequestError } from '../../queries/api';
import { useAuth } from '../../contexts/AuthContext';
import { useIsShopify, itemIdentityColumns } from '../../components/grid/productIdentity';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { useProductGrouping } from '../../components/grid/productGrouping';
import { useReportFilters } from '../../hooks/useReportFilters';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, copyOnlyContextMenuItems, restoreInitialSort } from '../../utils/gridDefaults';
import GridExportButton from '../../components/common/GridExportButton';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows } from '../../utils/pinnedTotals';
import '../../styles/pages.css';
import { GridEmptyState } from '../../components/grid/NoRowsOverlay';
import { GridPage, GridViewport } from '../../components/grid/GridPage';


function ClassificationReport() {
  const { t } = useTranslation();
  const { userContext } = useAuth();
  const isStaff = userContext?.user?.is_staff ?? false;
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber } = useFormatters();
  const defaultHidden = useMemo(() => ['shopify_product_title'], []);
  // Group variants by product (productGrouping): the switch, the product
  // tree, the row id and the saved column state, one preference for every
  // report. A product row carries the report's totals restricted to that
  // product and the report's own measure over it; only those columns say
  // `sortable: true`, since the server orders the products by them.
  const grouping = useProductGrouping({
    gridId: 'report_classification',
    preferenceKey: 'reports_grouped',
    defaultHidden,
    leafIdFields: ['item_id', 'location_id'],
  });
  const { isGrouped, columns: groupedColumns, variantProductColumn, totalsLabelField } = grouping;
  const gridDefaultColDef = useMemo(
    () => ({ ...defaultColDef, ...grouping.defaultColDef }),
    [grouping.defaultColDef],
  );
  const gridRef = useRef(null);

  const itemFilter = useItemFilter();
  const { location, setLocation, productCategory, setProductCategory } = useReportFilters('classification');
  const [kpiFilter, setKpiFilter] = useState(null);

  const params = useMemo(() => ({ location_code: location, product_category_id: productCategory?.id || null, ...itemFilter.params }), [location, productCategory, itemFilter.params]);
  const [chrome, setChrome] = useState({
    summary: null, currency: null, totals: null,
  });
  const onData = useCallback((data) => {
    setChrome({
      summary: data.summary ?? null,
      currency: data.currency ?? null,
      totals: data.totals ?? null,
    });
  }, []);
  const { onGridReady, onGridPreDestroyed, loading, error, pagination } = useSsrmGrid({
    endpoint: '/api/reports/classification/data/',
    params,
    onData,
    gridRef,
  });
  const { summary, currency, totals } = chrome;

  // Server-side export of the full filtered dataset.
  const exportProps = {
    endpoint: '/api/reports/classification/export/',
    filename: 'abc_xyz_classification',
    getParams: useCallback(
      (scope) => (scope === 'all' ? '' : buildExportQuery(params)),
      [params],
    ),
  };

  const data = useMemo(() => ({ summary, currency }), [summary, currency]);

  const handleKpiClick = useCallback((key) => {
    const newKey = kpiFilter === key ? null : key;
    setKpiFilter(newKey);

    const api = gridRef.current;
    if (!api) return;
    if (newKey === 'a_revenue') {
      api.applyColumnState({ state: [{ colId: 'revenue', sort: 'desc' }], defaultState: { sort: null } });
    } else {
      restoreInitialSort(api);
    }
  }, [kpiFilter]);

  const kpiItems = useMemo(() => {
    if (!data?.summary) return [];
    const s = data.summary;
    const total = s.total_items || 1;
    const pct = (v) => Math.round((v / total) * 100);
    return [
      { label: `A ${t('reports.classification.kpi.items')}`, value: pct(s.a_items), suffix: '%', key: 'a_class', tooltip: t('reports.classification.kpi.aItemsTooltip') },
      { label: `B ${t('reports.classification.kpi.items')}`, value: pct(s.b_items), suffix: '%', key: 'b_class', tooltip: t('reports.classification.kpi.bItemsTooltip') },
      { label: `C ${t('reports.classification.kpi.items')}`, value: pct(s.c_items), suffix: '%', key: 'c_class', tooltip: t('reports.classification.kpi.cItemsTooltip') },
      { label: t('reports.classification.kpi.aRevenue'), value: s.a_revenue_pct, suffix: '%', key: 'a_revenue', tooltip: t('reports.classification.kpi.aRevenueTooltip') },
    ];
  }, [data, t]);

  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, totalsLabelField('item_code'), t('grid.totals.label')),
    [totals, t, totalsLabelField],
  );

  const currencySuffix = currency ? ` (${currency})` : '';
  const decimalFormatter = useCallback(
    (p) => p.value != null ? formatNumber(p.value, 2) : '—',
    [formatNumber]
  );
  const pctFormatter = useCallback(
    (p) => p.value != null ? `${formatNumber(p.value, 1)}%` : '—',
    [formatNumber]
  );

  const columnDefs = useMemo(() => wrapColumnsForSummaryRows([
    ...groupedColumns([variantProductColumn()]),
    ...(isGrouped ? [] : itemIdentityColumns(isShopify, {
      productHeader: t('reports.columns.product'),
      codeHeader: t('reports.columns.itemCode'),
      nameHeader: t('reports.columns.itemName'),
    })),
    { field: 'category_label', headerName: t('reports.columns.productCategory'), minWidth: 100 },
    { field: 'location_name', headerName: t('reports.columns.location'), minWidth: 100 },
    {
      field: 'abc_class', headerName: t('reports.classification.columns.abcClass'), width: 70,
      type: 'centeredColumn',
      headerTooltip: t('reports.classification.tooltips.abcClass'),
    },
    {
      field: 'xyz_class', headerName: t('reports.classification.columns.xyzClass'), width: 70,
      type: 'centeredColumn',
      headerTooltip: t('reports.classification.tooltips.xyzClass'),
    },
    {
      field: 'combined_class', headerName: t('reports.classification.columns.combined'), width: 80,
      type: 'centeredColumn',
      headerTooltip: t('reports.classification.tooltips.combined'),
    },
    { field: 'revenue', sortable: true, initialSort: 'desc', headerName: t('reports.classification.columns.revenue') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'revenue_share_pct', headerName: t('reports.classification.columns.revenueShare'), type: 'decimalColumn', valueFormatter: pctFormatter },
    { field: 'cumulative_revenue_pct', headerName: t('reports.classification.columns.cumulativeRevenue'), type: 'decimalColumn', valueFormatter: pctFormatter },
    ...(isStaff ? [
      { field: 'mape_pct', headerName: t('reports.classification.columns.mape'), type: 'decimalColumn', valueFormatter: pctFormatter, headerTooltip: t('common.tooltips.forecastErrorPct'), headerClass: 'staff-column' },
      { field: 'cv_pct', headerName: t('reports.classification.columns.cv'), type: 'decimalColumn', valueFormatter: pctFormatter, headerTooltip: t('reports.classification.tooltips.cv'), headerClass: 'staff-column' },
      { field: 'mean_demand', headerName: t('reports.classification.columns.meanDemand'), type: 'decimalColumn', headerTooltip: t('reports.classification.tooltips.meanDemand'), headerClass: 'staff-column' },
      { field: 'std_dev', headerName: t('reports.classification.columns.stdDev'), type: 'decimalColumn', headerTooltip: t('reports.classification.tooltips.stdDev'), headerClass: 'staff-column' },
    ] : []),
    { field: 'target_stock', sortable: true, headerName: t('reports.classification.columns.targetStock'), type: 'numberColumn', headerTooltip: t('common.tooltips.targetStock') },
    ...(isStaff ? [
      { field: 'service_level_pct', headerName: t('reports.classification.columns.serviceLevel'), type: 'decimalColumn', valueFormatter: pctFormatter, headerTooltip: t('common.tooltips.serviceLevel'), headerClass: 'staff-column' },
    ] : []),
    { field: 'lead_time', headerName: t('reports.classification.columns.leadTime'), type: 'numberColumn', headerTooltip: t('common.tooltips.leadTime') },
  ]), [isShopify, isGrouped, groupedColumns, variantProductColumn, isStaff, t, currencySuffix, decimalFormatter, pctFormatter]);

  return (
    <div className="page-container">
      <ReportKpiStrip title={t('reports.classification.kpi.title')} items={kpiItems} loading={loading} activeKey={kpiFilter} onCardClick={handleKpiClick} storageKey="classification" />
      <PageBanner severity="error" messages={[error && translateRequestError(error, t, 'common.fetchError')]} />
      <div className="tiles-row">
        <Tile width="full" title={t('nav.reportClassification')} headerActions={<GridExportButton {...exportProps} />}>
          <GridPage>
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: productCategory, set: setProductCategory, show: true }}
              locationFilter={{ selected: location, set: setLocation }}
              actions={grouping.toggle}
            />
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  filtersActive={location !== 'all' || !!productCategory || !!itemFilter.filter || !!kpiFilter}
                >
                  <AgGridReact
                    theme={gridTheme} columnDefs={columnDefs} columnTypes={columnTypes}
                    loading={loading}
                    {...defaultGridProps} defaultColDef={gridDefaultColDef}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    {...grouping.gridProps}
                    rowModelType="serverSide"
                    {...pagination}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    pinnedBottomRowData={pinnedBottomRowData}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>
    </div>
  );
}

export default ClassificationReport;
